/** SCAPI's evaluated, localized Page Meta Tag Rule result. */
export type PageMetaTag = {
    id?: string;
    type?: string;
    value?: string;
};

export type PageSeoInput = {
    pageMetaTags?: readonly PageMetaTag[];
    pageTitle?: string | null;
    pageDescription?: string | null;
    title?: string;
    description?: string;
    rawTitle?: boolean;
};

/** Resolve each field separately. SFCC owns the complete authored title, including branding. */
export function resolvePageSeo({
    pageMetaTags,
    pageTitle,
    pageDescription,
    title,
    description,
    rawTitle,
}: PageSeoInput) {
    const ruleTitle = pageMetaTags?.find(
        (tag) => (tag.type === 'title' || (!tag.type && tag.id === 'title')) && tag.value?.trim()
    )?.value;
    const ruleDescription = pageMetaTags?.find(
        (tag) => tag.id === 'description' && (!tag.type || tag.type === 'name') && tag.value?.trim()
    )?.value;
    const authoredTitle = ruleTitle || (pageTitle?.trim() ? pageTitle : undefined);

    return {
        title: authoredTitle || title,
        description: ruleDescription || (pageDescription?.trim() ? pageDescription : undefined) || description,
        rawTitle: Boolean(authoredTitle || rawTitle),
    };
}

/**
 * Builds the full document `<title>` string from a resolved base title and the site name.
 * When `rawTitle` is set the title is used verbatim; otherwise ` | {siteName}` is appended.
 * When no `title` is provided the site name alone is used. Shared by `SeoMeta` (which drives
 * the `<title>` tag) and analytics, so the `page_view` title always matches the rendered tag.
 */
export function buildSeoTitle({
    title,
    rawTitle,
    siteName,
}: {
    title?: string;
    rawTitle?: boolean;
    siteName: string;
}): string {
    if (!title) {
        return siteName;
    }
    return rawTitle ? title : `${title} | ${siteName}`;
}
